import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Avatar,
  Box,
  Button,
  Flex,
  HStack,
  Spinner,
  Text,
  Switch,
  useColorMode,
} from "@chakra-ui/react";
import { FaSearch } from "react-icons/fa";
import { IconWrapper } from "./IconWrapper";
import { getThemeClasses } from "../utils/themeUtils";
import { showToast } from "../utils/toastUtils";
import { logToSystem } from "../utils/systemLogging";
import {
  fetchOrgProjectAccess,
  saveProjectAccess,
} from "../utils/projectAccess";

type AccessUser = {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
};

type AccessProject = {
  id: string;
  name: string;
  assignedUsers?: string[];
};

interface ProjectAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  project: AccessProject | null;
  users: AccessUser[];
  currentUser: any;
  /** Called after a successful save so callers can refresh their own state. */
  onSaved?: () => void;
}

const ProjectAccessModal: React.FC<ProjectAccessModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  project,
  users,
  currentUser,
  onSaved,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  /** What is stored right now, so we can save only what actually changed. */
  const [initialLevels, setInitialLevels] = useState<Record<string, boolean>>({});
  const [levels, setLevels] = useState<Record<string, boolean>>({});

  // Access is only ever granted to members already assigned to the project, so
  // the dialog lists exactly those members and nobody else.
  const assignedMembers = useMemo(() => {
    const assigned = new Set(project?.assignedUsers || []);
    return users
      .filter((user) => assigned.has(user.id))
      .sort((a, b) =>
        `${a.firstName || ""} ${a.lastName || ""}`.localeCompare(
          `${b.firstName || ""} ${b.lastName || ""}`
        )
      );
  }, [users, project]);

  const loadAccess = useCallback(async () => {
    if (!organizationId || !project) return;
    setIsLoading(true);
    try {
      const orgAccess = await fetchOrgProjectAccess(organizationId);
      const current = assignedMembers.reduce((acc, user) => {
        acc[user.id] = orgAccess[user.id]?.[project.id] || false;
        return acc;
      }, {} as Record<string, boolean>);
      setInitialLevels(current);
      setLevels(current);
    } catch (error) {
      console.error("Error loading project access:", error);
      showToast({
        title: "Could not load access",
        description: "Please close the dialog and try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, project, assignedMembers]);

  useEffect(() => {
    if (!isOpen) return;
    setSearchQuery("");
    loadAccess();
  }, [isOpen, loadAccess]);

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return assignedMembers;
    return assignedMembers.filter((user) =>
      `${user.firstName || ""} ${user.lastName || ""} ${user.email || ""}`
        .toLowerCase()
        .includes(query)
    );
  }, [assignedMembers, searchQuery]);

  const changes = useMemo(() => {
    return Object.entries(levels).reduce((acc, [userId, hasEditor]) => {
      if ((initialLevels[userId] || false) !== (hasEditor || false)) {
        acc[userId] = hasEditor;
      }
      return acc;
    }, {} as Record<string, boolean>);
  }, [levels, initialLevels]);

  const changeCount = Object.keys(changes).length;

  const toggleEditorAccess = (userId: string) => {
    setLevels((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const handleSave = async () => {
    if (!project || changeCount === 0 || isSaving) return;
    setIsSaving(true);
    try {
      await saveProjectAccess(organizationId, project.id, changes);
      setInitialLevels(levels);

      try {
        await logToSystem(organizationId, "actions", {
          type: "ACTIVITY",
          user: `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim(),
          email: currentUser?.email,
          action: `Updated project access for ${project.name}`,
          details: `Changed project access level for ${changeCount} user(s).`,
          status: "info",
        });
      } catch (logError) {
        console.error("Error logging project access change:", logError);
      }

      showToast({
        title: "Access updated",
        description: `Project access saved for ${changeCount} user(s).`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onSaved?.();
      onClose();
    } catch (error) {
      console.error("Error saving project access:", error);
      showToast({
        title: "Save failed",
        description: "Could not update project access. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!project) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered scrollBehavior="inside">
      <ModalOverlay
        bg={isDark ? "blackAlpha.700" : "blackAlpha.500"}
        backdropFilter="blur(5px)"
      />
      <ModalContent
        borderRadius="lg"
        bg={isDark ? "#1A1C1F" : "white"}
        border="1px solid"
        borderColor={isDark ? "#2E2F33" : "#E5E7EB"}
      >
        <ModalHeader
          color={isDark ? "#E8C16C" : "#111827"}
          fontSize="lg"
          fontWeight="bold"
        >
          Project Access — {project.name}
        </ModalHeader>
        <ModalCloseButton color={isDark ? "#C3C9D3" : "#111827"} />

        <ModalBody px={5} pb={2}>
          <div
            className={`relative flex items-center w-full rounded-full ${theme.cardMuted} overflow-hidden mb-3`}
          >
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <IconWrapper
                icon={FaSearch}
                className={`h-4 w-4 ${theme.textMuted}`}
                size={16}
              />
            </div>
            <input
              type="text"
              placeholder="Search assigned users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`${theme.searchInput} block w-full pl-10 pr-3 py-1.5 border rounded-full leading-5 focus:outline-none sm:text-sm`}
            />
          </div>

          {isLoading ? (
            <Flex justify="center" align="center" py={10}>
              <Spinner color="#B59143" />
            </Flex>
          ) : filteredUsers.length === 0 ? (
            <Text
              textAlign="center"
              py={8}
              fontSize="sm"
              className={theme.textMuted}
            >
              {assignedMembers.length === 0
                ? "No users are assigned to this project yet. Assign users to the project first, then set their access here."
                : "No assigned users match your search."}
            </Text>
          ) : (
            <Box>
              {filteredUsers.map((user) => {
                const hasEditor = levels[user.id] || false;
                return (
                  <Flex
                    key={user.id}
                    align="center"
                    justify="space-between"
                    gap={3}
                    py={2.5}
                    borderBottomWidth="1px"
                    borderColor={isDark ? "#2E2F33" : "#E5E7EB"}
                  >
                    <HStack spacing={2} minW={0} flex="1">
                      <Avatar
                        size="xs"
                        name={`${user.firstName || ""} ${user.lastName || ""}`}
                      />
                      <Box minW={0}>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          noOfLines={1}
                          color={isDark ? "white" : "gray.900"}
                        >
                          {`${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                            user.email ||
                            user.id}
                        </Text>
                        <Text fontSize="xs" noOfLines={1} className={theme.textMuted}>
                          {user.email}
                        </Text>
                      </Box>
                    </HStack>

                    <HStack spacing={2} flexShrink={0}>
                      <Text
                        fontSize="xs"
                        fontWeight="medium"
                        color={isDark ? "#C3C9D3" : "gray.600"}
                      >
                        Editor
                      </Text>
                      <Switch
                        size="sm"
                        colorScheme="yellow"
                        isChecked={hasEditor}
                        onChange={() => toggleEditorAccess(user.id)}
                      />
                    </HStack>
                  </Flex>
                );
              })}
            </Box>
          )}
        </ModalBody>

        <ModalFooter gap={3}>
          <Text fontSize="xs" mr="auto" className={theme.textMuted}>
            {changeCount > 0
              ? `${changeCount} unsaved change${changeCount > 1 ? "s" : ""}`
              : "No changes"}
          </Text>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            isDisabled={isSaving}
            color={isDark ? "#C3C9D3" : "gray.700"}
          >
            Cancel
          </Button>
          <button
            onClick={handleSave}
            disabled={changeCount === 0 || isSaving || isLoading}
            className={`px-4 py-1.5 rounded-full text-sm ${theme.primaryButton} ${
              changeCount === 0 || isSaving || isLoading
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          >
            {isSaving ? "Saving..." : "Save Access"}
          </button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ProjectAccessModal;
